var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { generateUUID } from '@utils';
import { UXPContext } from "@core/context/Context";
import Alert from './Alert';
import AlertContext from './AlertContext';
import ConfirmAlert from './ConfirmAlert';
import FormAlert from './Form';
import PortalContainer from "../../widget/popups/PortalContainer";
const AlertProvider = (props) => {
    const context = React.useContext(UXPContext);
    // Use state to trigger re-renders and ref to manage alerts
    const [forceRender, setForceRender] = React.useState(false);
    const alertRef = React.useRef([]);
    // Update alerts and trigger re-render
    const updateAlerts = (newAlerts) => {
        alertRef.current = newAlerts;
        setForceRender((prev) => !prev); // Force re-render
    };
    // Remove an alert by ID
    const remove = React.useCallback((id) => {
        const _alerts = alertRef.current.filter((alert) => alert.id !== id);
        updateAlerts(_alerts);
    }, []);
    // Create an alert with default props
    const createAlert = (type, defaultProps, content) => (Object.assign(Object.assign({ id: generateUUID(), type }, defaultProps), (typeof content === 'string' ? { content } : content)));
    // Default props for confirm alerts
    const defaultConfirmProps = {
        title: context.$L('uxp-core.text.confirm'),
        content: context.$L('uxp-core.text.are-you-sure'),
        confirmButtonTitle: context.$L('uxp-core.text.confirm'),
        cancelButtonTitle: context.$L('uxp-core.text.cancel'),
    };
    // Default props for regular alerts
    const defaultAlertProps = {
        title: context.$L('uxp-core.text.alert'),
        content: 'content',
        autoClose: false,
        closeAfter: 2000,
        showCloseBtn: true,
        closeButtonTitle: context.$L('uxp-core.text.close'),
    };
    // Default props for form alerts
    const defaultFormProps = {
        title: 'Form',
        content: '',
        submitButtonTitle: context.$L('uxp-core.text.save'),
        cancelButtonTitle: context.$L('uxp-core.text.cancel'),
        formStructure: [],
        hideCancelButton: false,
    };
    // Show a confirm alert
    const confirm = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        // With `fields`, resolve to the collected values object (false on cancel); otherwise keep the
        // classic boolean contract so existing callers are unaffected.
        const hasFields = typeof content === 'object' && Array.isArray(content.fields) && content.fields.length > 0;
        return new Promise((done) => {
            const newConfirm = createAlert('confirm', Object.assign(Object.assign({}, defaultConfirmProps), { onConfirm: (values) => done(hasFields ? (values !== null && values !== void 0 ? values : {}) : true), onCancel: () => done(false) }), content);
            // Set smart defaults if requireVerification is true
            if (newConfirm.requireVerification) {
                if (!newConfirm.verificationText) {
                    newConfirm.verificationText = 'confirm';
                }
                if (!newConfirm.verificationTitle) {
                    newConfirm.verificationTitle = context.$L('uxp-core.text.verification-required');
                }
                if (!newConfirm.verificationConfirmButtonTitle) {
                    newConfirm.verificationConfirmButtonTitle = context.$L('uxp-core.text.confirm');
                }
            }
            updateAlerts([...alertRef.current, newConfirm]);
        });
    }), [context]);
    // Show a default alert
    const show = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newAlert = createAlert('alert', Object.assign(Object.assign({}, defaultAlertProps), { variant: 'default', onClose: () => done('closed') }), content);
            // Show close button if not auto-closing
            if (!newAlert.autoClose && !newAlert.showCloseBtn) {
                newAlert.showCloseBtn = true;
            }
            newAlert.createdAt = new Date().getTime();
            updateAlerts([...alertRef.current, newAlert]);
            // Auto-close if enabled
            if (newAlert.autoClose && newAlert.closeAfter) {
                setTimeout(() => {
                    remove(newAlert.id);
                }, newAlert.closeAfter);
            }
        });
    }), [context, remove]);
    // Show an error alert
    const error = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newAlert = createAlert('alert', Object.assign(Object.assign({}, defaultAlertProps), { variant: 'error', onClose: () => done('closed') }), content);
            updateAlerts([...alertRef.current, newAlert]);
            // Auto-close if enabled
            if (newAlert.autoClose && newAlert.closeAfter) {
                setTimeout(() => {
                    remove(newAlert.id);
                }, newAlert.closeAfter);
            }
        });
    }), [context, remove]);
    // Show an info alert
    const info = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newAlert = createAlert('alert', Object.assign(Object.assign({}, defaultAlertProps), { variant: 'info', onClose: () => done('closed') }), content);
            updateAlerts([...alertRef.current, newAlert]);
            // Auto-close if enabled
            if (newAlert.autoClose && newAlert.closeAfter) {
                setTimeout(() => {
                    remove(newAlert.id);
                }, newAlert.closeAfter);
            }
        });
    }), [context, remove]);
    // Show a warning alert
    const warning = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newAlert = createAlert('alert', Object.assign(Object.assign({}, defaultAlertProps), { variant: 'warning', onClose: () => done('closed') }), content);
            updateAlerts([...alertRef.current, newAlert]);
            // Auto-close if enabled
            if (newAlert.autoClose && newAlert.closeAfter) {
                setTimeout(() => {
                    remove(newAlert.id);
                }, newAlert.closeAfter);
            }
        });
    }), [context, remove]);
    // Show a success alert
    const success = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newAlert = createAlert('alert', Object.assign(Object.assign({}, defaultAlertProps), { variant: 'success', onClose: () => done('closed') }), content);
            updateAlerts([...alertRef.current, newAlert]);
            // Auto-close if enabled
            if (newAlert.autoClose && newAlert.closeAfter) {
                setTimeout(() => {
                    remove(newAlert.id);
                }, newAlert.closeAfter);
            }
        });
    }), [context, remove]);
    // Show a form alert
    const form = React.useCallback((content) => __awaiter(void 0, void 0, void 0, function* () {
        return new Promise((done) => {
            const newForm = createAlert('form', Object.assign(Object.assign({}, defaultFormProps), { onSubmit: (data) => done(data), onCancel: () => done(null) }), content);
            updateAlerts([...alertRef.current, newForm]);
        });
    }), [context]);
    // Provider value
    const providerValue = React.useMemo(() => ({ show, confirm, form, error, info, warning, success }), [
        show, confirm, form, error, info, warning, success
    ]);
    // Render the current alert
    const renderAlert = () => {
        if (alertRef.current.length === 0)
            return null;
        const _alert = alertRef.current[0];
        return (_jsx(PortalContainer, { disableScroll: true, hasBackdrop: true, children: _jsxs("div", { className: "uxpcore_alert-container", children: [_alert.type === 'alert' && _jsx(Alert, Object.assign({}, _alert, { remove: remove }), _alert.id), _alert.type === 'confirm' && _jsx(ConfirmAlert, Object.assign({}, _alert, { remove: remove }), _alert.id), _alert.type === 'form' && _jsx(FormAlert, Object.assign({}, _alert, { remove: remove }), _alert.id)] }) }));
    };
    return (_jsxs(AlertContext.Provider, { value: providerValue, children: [alertRef.current.length > 0 && renderAlert(), props.children] }));
};
export default AlertProvider;
